import { useCallback, useMemo, useRef, useState } from 'react'
import type { TruckConfiguration } from '#app/store/slices/configurationSlice'
import type { ConfigurationEnrichmentData } from '#app/store/slices/enrichConfiguration'
import type { AppProductGroup, ProductPath } from '#app/types/AppTypes'
import type { ComparisonDisplayMode } from '#pages/Configuration/Comparison/ComparisonTableContext'
import { isProductFamily, isProductGroup } from '#utils/EntryTypeGuards'
import type { FilterState } from '../Details/Filter/FilterState'
import { getAllGroups } from '../utils/collections'
import type { ConfigurationDiff } from './useConfigurationDifference'
import { type ComparisonDataEntry, useLinearizeComparisonData } from './useLinearizeComparisonData'

/* Learning Log
  - tanstack virtual does not have a built-in filtering solution
  - it requires the measure element to have a data attribute: `data-index={index}`
  - annoying: splitting the row positioning and data-index attribute from the actual row
              maybe convenient to have an abstracted row that passes the rendering somehow
              (which then causes problems with subgrids)
*/

export const useComparisonTable = ({
  baseConfiguration,
  comparisonConfiguration,
  diff,
  displayMode,
  filterState,
}: {
  baseConfiguration: { configuration: TruckConfiguration; enrichment: ConfigurationEnrichmentData } | undefined
  comparisonConfiguration: { configuration: TruckConfiguration; enrichment: ConfigurationEnrichmentData } | undefined
  diff: ConfigurationDiff | undefined
  displayMode: ComparisonDisplayMode
  filterState: FilterState
}) => {
  const { filteredLinearizedEntries, allLinearizedEntries } = useLinearizeComparisonData({
    baseConfiguration,
    comparisonConfiguration,
    diff,
    filter: filterState,
  })

  const canBeSelectedRef = useRef<Record<ProductPath, boolean>>({})

  const [selectedEntries, setSelectedEntries] = useState<Partial<Record<ProductPath, boolean>>>({})
  const setEntrySelection = useCallback(
    (path: ProductPath, selected: boolean) => {
      const entry = filteredLinearizedEntries.find((x) => x.id === path)
      if (!entry) {
        return
      }

      const updatedSelectedEntries = { ...selectedEntries }
      if (!canBeSelectedRef.current[path]) {
        return
      }
      updatedSelectedEntries[path] = selected
      if (!diff) {
        return
      }
      selectedEntries[path] = selected

      const baseEntry = baseConfiguration?.enrichment.byPath[path]
      // they have to be both product groups, but that makes TS behave better
      if (isProductGroup(baseEntry)) {
        const subgroups = getAllGroups(baseEntry)
        // select each group, groups contain all baseEntry groups and comparisonEntryGroups
        for (const subgroup of subgroups) {
          if (!canBeSelectedRef.current[subgroup.path]) {
            continue
          }
          updatedSelectedEntries[subgroup.path] = selected
          // select each family
          for (const family of subgroup.families) {
            if (!canBeSelectedRef.current[family.path]) {
              continue
            }
            updatedSelectedEntries[family.path] = selected
          }
        }
        // check if the parent group has been fully selected
        if (baseEntry.parentGroup) {
          let parentGroup: AppProductGroup | undefined = baseEntry.parentGroup
          while (parentGroup) {
            updatedSelectedEntries[parentGroup.path] = parentGroup.subGroups.every((subgroup) => updatedSelectedEntries[subgroup.path])
            parentGroup = parentGroup.parentGroup
          }
        }
      }

      if (isProductFamily(baseEntry)) {
        if (!canBeSelectedRef.current[baseEntry.path]) {
          return
        }

        if (!selected) {
          // walk through all parent groups and unselect them
          let parentGroup: AppProductGroup | undefined = baseEntry.parentGroup
          while (parentGroup) {
            updatedSelectedEntries[parentGroup.path] = selected
            parentGroup = parentGroup.parentGroup
          }
        } else {
          const allGroupFamiliesSelected = baseEntry.parentGroup.families.every(
            (neighborFamily) => !diff.affectedFamilies.has(neighborFamily.id) || updatedSelectedEntries[neighborFamily.path],
          )
          // write all selection for parent groups and their parents
          if (allGroupFamiliesSelected) {
            updatedSelectedEntries[baseEntry.parentGroup.path] = selected
            let parentParentGroup = baseEntry.parentGroup.parentGroup
            while (parentParentGroup) {
              if (parentParentGroup.subGroups.every((subgroup) => updatedSelectedEntries[subgroup.path])) {
                updatedSelectedEntries[parentParentGroup.path] = selected
              }
              parentParentGroup = parentParentGroup.parentGroup
            }
          }
        }
      }
      setSelectedEntries(updatedSelectedEntries)
    },
    [baseConfiguration, diff, selectedEntries, filteredLinearizedEntries],
  )

  const displayedEntries = useMemo((): ComparisonDataEntry[] => {
    let entries = filteredLinearizedEntries

    switch (displayMode) {
      case 'all':
        entries = filteredLinearizedEntries
        break
      case 'price-differences':
        entries = filteredLinearizedEntries.filter((entry) => entry.priceChanged)
        break
      case 'differences':
        entries = filteredLinearizedEntries.filter((entry) => entry.affected || entry.missing)
        break
      case 'differences-no-missing':
        entries = filteredLinearizedEntries.filter((entry) => entry.affected)
        break
      case 'selected':
        entries = filteredLinearizedEntries.filter((entry) => {
          switch (entry.type) {
            case 'group': {
              const isGroupSelected = selectedEntries[entry.id]
              if (isGroupSelected) {
                return true
              }
              const subgroups = getAllGroups(entry.base)
              for (const subgroup of subgroups) {
                const isSubgroupSelected = selectedEntries[subgroup.path]
                if (isSubgroupSelected) {
                  return true
                }

                for (const family of subgroup.families) {
                  const isFamilySelected = selectedEntries[family.path]
                  if (isFamilySelected) {
                    return true
                  }
                }
              }
              return false
            }
            case 'family':
              return selectedEntries[entry.id]
          }
          return false
        })
        break
    }

    const groupPathsWithFamilies = new Set<ProductPath>()
    for (const entry of entries) {
      if (entry.type === 'family') {
        const baseFamily = entry.base
        if (baseFamily?.parentGroup) {
          groupPathsWithFamilies.add(baseFamily.parentGroup.path)
          if (baseFamily.parentGroup.parentGroup) {
            groupPathsWithFamilies.add(baseFamily.parentGroup.parentGroup.path)
          }
          // continue, as the base family tree is the one we want to display
          continue
        }
        const comparisonFamily = entry.comparison
        if (comparisonFamily?.parentGroup) {
          groupPathsWithFamilies.add(comparisonFamily.parentGroup.path)
          if (comparisonFamily.parentGroup.parentGroup) {
            groupPathsWithFamilies.add(comparisonFamily.parentGroup.parentGroup.path)
          }
        }
      }
    }

    return entries.filter((e) => (e.type === 'group' ? groupPathsWithFamilies.has(e.id) : true))
  }, [filteredLinearizedEntries, displayMode, selectedEntries])

  const { priceDeltas, canSelect } = useMemo(() => {
    const pricesByPath: Record<ProductPath, number> = {}
    let maxDelta = 0
    let totalDelta = 0

    const canSelect: Record<ProductPath, boolean> = {}

    // first pass to determine families that are affected
    for (const entry of allLinearizedEntries) {
      if (entry.type === 'family') {
        if (entry.affected && entry.base && entry.comparison) {
          const selectedComparisonVariantId = entry.comparison.selectedVariant?.id
          const canApplyVariantSelection = entry.base.variants.find((baseVariant) => baseVariant.id === selectedComparisonVariantId) !== undefined
          canSelect[entry.id] = canApplyVariantSelection
        }

        const origin = entry.base ?? entry.comparison
        if (!origin) {
          pricesByPath[entry.id] = 0
          break
        }

        const basePrice = baseConfiguration?.enrichment.enrichedFamilies[origin.id]?.selectedVariant?.priceData?.price ?? 0
        const comparisonPrice = comparisonConfiguration?.enrichment.enrichedFamilies[origin.id]?.selectedVariant?.priceData?.price ?? 0
        const delta = comparisonPrice - basePrice
        totalDelta += delta
        maxDelta = Math.max(Math.abs(delta), maxDelta)
        pricesByPath[entry.id] = delta
      }
    }

    for (const entry of allLinearizedEntries) {
      if (entry.type === 'group') {
        const origin = entry.base ?? entry.comparison
        if (!origin) {
          pricesByPath[entry.id] = 0
          break
        }

        const withSubGroups = getAllGroups(origin)
        let accumulator = 0
        let anySubGroupCanBeSelected = false
        for (const group of withSubGroups) {
          let anyFamilyCanBeSelected = false
          for (const family of group.families) {
            const basePrice = baseConfiguration?.enrichment.enrichedFamilies[family.id]?.selectedVariant?.priceData?.price ?? 0
            const comparisonPrice = comparisonConfiguration?.enrichment.enrichedFamilies[family.id]?.selectedVariant?.priceData?.price ?? 0
            const delta = comparisonPrice - basePrice
            accumulator += delta
            anyFamilyCanBeSelected = anyFamilyCanBeSelected || canSelect[family.path]
          }
          canSelect[group.path] = anyFamilyCanBeSelected
          anySubGroupCanBeSelected = anySubGroupCanBeSelected || anyFamilyCanBeSelected
        }
        // maxDelta = Math.max(Math.abs(accumulator), maxDelta)
        pricesByPath[entry.id] = accumulator
        canSelect[entry.id] = anySubGroupCanBeSelected
      }
    }

    let selectedDelta = 0
    for (const [path, selected] of Object.entries(selectedEntries)) {
      if (!selected) {
        continue
      }
      const productPath = path as ProductPath
      // don't count groups, only families
      if (!isProductFamily(baseConfiguration?.enrichment.byPath[productPath])) {
        continue
      }
      selectedDelta += pricesByPath[path as ProductPath]
    }

    canBeSelectedRef.current = canSelect

    return { priceDeltas: { byPath: pricesByPath, maximumDelta: maxDelta, totalDelta, selectedDelta }, canSelect }
  }, [baseConfiguration?.enrichment, comparisonConfiguration?.enrichment, allLinearizedEntries, selectedEntries])

  const status = useMemo(() => {
    if (baseConfiguration?.configuration === undefined) {
      return 'base-missing' as const
    }
    if (baseConfiguration.configuration.step.system !== 'DetailedStep') {
      return 'base-wrong-step' as const
    }
    if (comparisonConfiguration?.configuration === undefined) {
      return 'comparison-missing' as const
    }
    if (comparisonConfiguration.configuration.step.system !== 'DetailedStep') {
      return 'comparison-wrong-step' as const
    }
    return 'ready' as const
  }, [baseConfiguration, comparisonConfiguration])

  return {
    linearizedEntries: filteredLinearizedEntries,
    displayedEntries,
    priceDeltas,
    canBeSelected: canSelect,
    status,
    setSelectedEntries,
    setEntrySelection,
    selectedEntries,
  }
}
